import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';

import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgModule, Injector, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { DxHttpModule } from 'devextreme-angular/http';
import { SideNavOuterToolbarModule, SideNavInnerToolbarModule, SingleCardModule } from './layouts';
import { FooterModule, ResetPasswordFormModule, CreateAccountFormModule, ChangePasswordFormModule } from './shared/components';
import { AuthService, ScreenService, AppInfoService } from './shared/services';
import { FeatureFlagsService } from './shared/services/feature-flags.service';
import { TokenSessionService } from './shared/services/token-session.service';
import { SessionWarningComponent } from './shared/components/session-warning/session-warning.component';
import { TokenTestingComponent } from './shared/components/token-testing/token-testing.component';
import { UnauthenticatedContentModule } from './unauthenticated-content';
import { AppRoutingModule } from './app-routing.module';
import {
  DxFileUploaderModule, DxFileManagerModule, DxButtonModule, DxTooltipModule,
  DxValidatorModule, DxDataGridModule, DxLoadPanelModule,
  DxLookupModule
} from 'devextreme-angular';
import { GraphQLModule } from './graphql.module';
import { AppInjector } from './app-injector.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MessageBarModule } from './shared/components/framework-ui/navigation/message-bar/message-bar.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { TimezoneInterceptor } from './shared/interceptors/timezone.interceptor';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';

// Factory function for APP_INITIALIZER
export function initializeAuth(authService: AuthService) {
  return () => {
    // console.log('🏁 APP_INITIALIZER: Starting authentication check...');
    return authService.isAuthenticatedAsync().then(isAuth => {
      // console.log('🏁 APP_INITIALIZER: Authentication check complete. Result:', isAuth);
      return Promise.resolve();
    });
  };
}

// Factory function to load feature flags before the app boots so that UI
// surfaces (e.g. the request-type dropdown) can gate themselves synchronously
// without waiting for a runtime fetch.
//
// IMPORTANT: Angular runs APP_INITIALIZER factories in parallel, so we cannot
// rely on `initializeAuth` having completed before this runs. The FeatureFlags
// endpoint requires a Bearer token; if we issue the HTTP call before the OIDC
// access token is available the AuthInterceptor sends the request without an
// Authorization header and the API returns 401 (observed in UAT where the
// identity round-trip is slower than locally).
//
// Wait for the auth check to resolve and only attempt to load the flags when a
// token is actually available. When bootstrap completes pre-login (redirect to
// IdP), the deferred load in `AppComponent.loginChanged` covers the post-login
// path. Failures fall back to `DEFAULT_FLAGS` inside the service.
export function initializeFeatureFlags(
  authService: AuthService,
  featureFlags: FeatureFlagsService
) {
  return async () => {
    await authService.isAuthenticatedAsync();
    const token = await authService.getAccessToken();
    if (!token) {
      return;
    }
    await featureFlags.load();
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SessionWarningComponent,
    TokenTestingComponent
  ],
  imports: [
    StoreModule.forRoot({}),
    EffectsModule.forRoot({}),
    BrowserModule,
    CommonModule,
    FormsModule,
    DxHttpModule,
    SideNavOuterToolbarModule,
    SideNavInnerToolbarModule,
    SingleCardModule,
    FooterModule,
    ResetPasswordFormModule,
    CreateAccountFormModule,
    ChangePasswordFormModule,
    UnauthenticatedContentModule,
    AppRoutingModule,
    CommonModule,
    DxFileUploaderModule,
    DxButtonModule,
    DxFileManagerModule,
    DxTooltipModule,
    DxValidatorModule,
    DxDataGridModule,
    DxLoadPanelModule,
    GraphQLModule,
    DxLookupModule,
    MessageBarModule,
  ],
  providers: [
    AuthService,
    ScreenService,
    AppInfoService,
    TokenSessionService,
    DatePipe,
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimezoneInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      deps: [AuthService, FeatureFlagsService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private readonly injector: Injector) {
    AppInjector.getInstance().setInjector(injector);
  }
}
